import React from 'react';
import clsx from 'clsx';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import type { WizardQuestion, QuestionType, CollectFieldType } from './types';

interface Props {
  question: WizardQuestion;
  onChange: (q: WizardQuestion) => void;
}

const QUESTION_TYPE_OPTIONS: { value: QuestionType; label: string; desc: string }[] = [
  { value: 'choice', label: 'Multiple-choice question', desc: 'Customer picks from a list of options' },
  { value: 'collect', label: 'Ask for information', desc: 'Capture name, phone, email, number, or free text' },
  { value: 'handoff', label: 'Skip — send to a person', desc: 'Hand the conversation off immediately' },
];

const COLLECT_TYPE_OPTIONS: { value: CollectFieldType; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone number' },
  { value: 'email', label: 'Email address' },
  { value: 'number', label: 'A number' },
  { value: 'text', label: 'Free text' },
];

let _optionCounter = 0;
function newOptionId() {
  _optionCounter += 1;
  return `opt_${_optionCounter}`;
}

export const WizardQuestionBuilder: React.FC<Props> = ({ question, onChange }) => {
  const setType = (type: QuestionType) => {
    const base: WizardQuestion = { ...question, type, prompt: question.prompt };
    if (type === 'choice' && (!base.options || base.options.length === 0)) {
      base.options = [
        { id: newOptionId(), label: '', action: 'end' },
        { id: newOptionId(), label: '', action: 'end' },
      ];
    }
    if (type === 'collect' && !base.collectType) {
      base.collectType = 'text';
    }
    onChange(base);
  };

  const setPrompt = (prompt: string) => onChange({ ...question, prompt });

  const addOption = () => {
    const options = [...(question.options ?? []), { id: newOptionId(), label: '', action: 'end' as const }];
    onChange({ ...question, options });
  };

  const removeOption = (id: string) => {
    onChange({ ...question, options: question.options?.filter(o => o.id !== id) });
  };

  const updateOptionLabel = (id: string, label: string) => {
    onChange({
      ...question,
      options: question.options?.map(o => o.id === id ? { ...o, label } : o),
    });
  };

  return (
    <div className="space-y-4">
      {/* Type selector */}
      <div className="space-y-2">
        {QUESTION_TYPE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setType(opt.value)}
            className={clsx(
              'w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all',
              question.type === opt.value
                ? 'border-primary bg-primary-soft/60'
                : 'border-border bg-surface hover:border-primary/40',
            )}
          >
            <div
              className={clsx(
                'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
                question.type === opt.value
                  ? 'border-primary bg-primary'
                  : 'border-border-strong',
              )}
            />
            <div>
              <div className={clsx(
                'text-sm font-semibold',
                question.type === opt.value ? 'text-primary' : 'text-text-primary',
              )}>
                {opt.label}
              </div>
              <div className="text-xs text-text-muted">{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Prompt input (not shown for handoff) */}
      {question.type !== 'handoff' && (
        <FormField label="Question text" required>
          <Input
            value={question.prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={
              question.type === 'choice'
                ? 'e.g. What best describes your interest?'
                : 'e.g. What is your full name?'
            }
          />
        </FormField>
      )}

      {/* Choice options */}
      {question.type === 'choice' && (
        <div>
          <div className="mb-2 text-[13px] font-medium text-text-secondary">Answer options</div>
          <div className="space-y-2">
            {question.options?.map((opt, idx) => (
              <div key={opt.id} className="flex items-center gap-2">
                <span className="shrink-0 w-5 text-center text-xs font-mono text-text-muted">{idx + 1}</span>
                <Input
                  value={opt.label}
                  onChange={e => updateOptionLabel(opt.id, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  className="flex-1"
                />
                {(question.options?.length ?? 0) > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(opt.id)}
                    className="shrink-0 text-text-muted hover:text-danger transition-colors"
                    aria-label="Remove option"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {(question.options?.length ?? 0) < 6 && (
            <button
              type="button"
              onClick={addOption}
              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-dark transition-colors"
            >
              <Plus size={12} /> Add option
            </button>
          )}
        </div>
      )}

      {/* Collect type picker */}
      {question.type === 'collect' && (
        <FormField label="Type of information">
          <div className="flex flex-wrap gap-1.5">
            {COLLECT_TYPE_OPTIONS.map(ct => (
              <button
                key={ct.value}
                type="button"
                onClick={() => onChange({ ...question, collectType: ct.value })}
                className={clsx(
                  'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                  question.collectType === ct.value
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border text-text-secondary hover:border-primary/40',
                )}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </FormField>
      )}

      {/* Number range validation */}
      {question.type === 'collect' && question.collectType === 'number' && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Min value (optional)">
            <Input
              type="number"
              value={question.validationMin ?? ''}
              onChange={e => onChange({ ...question, validationMin: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="e.g. 0"
            />
          </FormField>
          <FormField label="Max value (optional)">
            <Input
              type="number"
              value={question.validationMax ?? ''}
              onChange={e => onChange({ ...question, validationMax: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="e.g. 100"
            />
          </FormField>
        </div>
      )}

      {/* Handoff note */}
      {question.type === 'handoff' && (
        <div className="rounded-xl border border-success/30 bg-success-soft p-3 text-sm text-text-secondary">
          The conversation will be handed off to your team right away — no further questions will be asked.
        </div>
      )}
    </div>
  );
};
